import React, { useState, useEffect, useRef } from 'react';
import styles from './GoalsDetailsPage.module.scss';
import Icon from '../../../common/Icon/Icon';
import { useAppSelector } from '../../../../redux/hooks';
import DropdownMenu from '../../../common/DropdownMenu/DropdownMenu';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import HTMLtoText from '../../../common/HTMLToText/HTMLToText';
import { useTranslation } from 'react-i18next';
import useClickOutsideMap from '../../../CustomHooks/useClickOutSideMap';
import { StatusDropdown } from '../../../common/StatusDropdown/StatusDropdown';
import { convertUrlsToLinks, useProfileParams } from '../../../../utils/common';
import {
  ACTIONS,
  countryCodeMappings,
  GOAL_ACTION_STATUS,
} from '../../../../constants/dataConstants';
import { formatDate } from '../../../../utils/dateFormatter';
import Engagements from '../Engagements/Engagements';
import GoalsNotesAndDocuments from '../GoalsNotesAndDocuments/GoalsNotesAndDocuments';
import { useUpdateGoalActionStatusMutation } from '../../../../redux/services/goalActionsAPI';
import isEmpty from 'lodash/isEmpty';
import { getGoalActionStatusLabel, getMetricLabel } from '../../../../utils/goalUtil';
import { useUpdateGoalMetricStatusMutation } from '../../../../redux/services/goalMetricAPI';
import { formatDateShort } from '../../../../utils/common';

type GoalsDetailsPageProps = {
  goalID: number;
  goalActions: ActionDataResp[];
  setGoalActions: React.Dispatch<React.SetStateAction<ActionDataResp[]>>;
  selectedMetricsMap: { [key: string]: MetricRow[] };
  setSelectedMetricsMap: React.Dispatch<React.SetStateAction<{ [key: string]: MetricRow[] }>>;
  handleOperation: (item: string, gridId: string | number) => void;
  selectedActionStatusItem: { [key: string]: string };
  setSelectedActionStatusItem: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  isHistoryGoalData: boolean;
};

const GoalsDetailsPage = ({
  goalID,
  goalActions,
  setGoalActions,
  selectedMetricsMap,
  setSelectedMetricsMap,
  handleOperation,
  selectedActionStatusItem,
  setSelectedActionStatusItem,
  isHistoryGoalData,
}: GoalsDetailsPageProps) => {
  const { t } = useTranslation();

  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const [showStatusDropdown, setShowStatusDropdown] = useState<{
    [key: string]: boolean;
  }>({});
  const [showMetricsDropdown, setShowMetricsDropdown] = useState<{
    [key: string]: boolean;
  }>({});
  const [selectedItem, setSelectedItem] = useState<string>('On Track');
  const [selectedMetricsItem, setSelectedMetricsItem] = useState<{
    [key: string]: string;
  }>({});
  const metricsDropdownRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const statusDropdownRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const userDetails = useAppSelector((state: any) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);
  const { affiliateCode, aboNumber } = useProfileParams();
  const [
    updateGoalActionStatus,
    { isLoading: isLoadingUpdateGoalActionstatus, isError: isErrorUpdateGoalActionStatus },
  ] = useUpdateGoalActionStatusMutation();

  const [
    updateGoalMetricStatus,
    { isLoading: isLoadingUpdateGoalMetricstatus, isError: isErrorUpdateGoalMetricStatus },
  ] = useUpdateGoalMetricStatusMutation();

  useEffect(() => {
    if (!isEmpty(goalActions)) {
      setSelectedMetricsMap((prev: any) => {
        // Create a copy of previous state to update
        const updated = { ...prev };

        goalActions.forEach((item) => {
          if (item && item.goalMetrics?.length > 0) {
            // Only update if the key doesn't already exist
            if (!updated[item.aboGoalActionId]) {
              updated[item.aboGoalActionId] = item.goalMetrics;
            }
          }
        });

        return updated;
      });
    }
  }, [goalActions]);

  const handleReadMoreToggle = (gridId: string) => {
    setExpandedIds((prev) =>
      prev.includes(gridId) ? prev.filter((id) => id !== gridId) : [...prev, gridId]
    );
  };

  useClickOutsideMap(statusDropdownRef, setShowStatusDropdown);
  useClickOutsideMap(metricsDropdownRef, setShowMetricsDropdown);

  const [modalOpen, setModalOpen] = useState(false);
  const [showViewAction, setShowViewAction] = useState(false);
  const [isHover, setIsHover] = useState<{
    [gridId: string]: { [idx: number]: boolean };
  }>({});

  const handleClickAddAction = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const toggleIsMet = (
    aboGoalActionId: string | number,
    aboGoalActionMetricId: number | null,
    status: number
  ) => {
    updateGoalMetricStatus({
      affiliateCode,
      aboNumber,
      goalMetricId: aboGoalActionMetricId,
      statusId: status === 1 ? 2 : 1,
    }).then((resp: any) => {
      const updateMetricStatusData = resp?.data?.body;
      if (!isEmpty(updateMetricStatusData)) {
        setSelectedMetricsMap((prev) => {
          const metrics = prev[aboGoalActionId] || [];
          const updatedMetrics: any = metrics.map((metric) =>
            metric.aboGoalActionMetricId === updateMetricStatusData.aboGoalActionMetricId
              ? updateMetricStatusData
              : metric
          );

          return {
            ...prev,
            [aboGoalActionId]: updatedMetrics,
          };
        });
      }
    });
  };
  const handleDeleteMetrics = (gridId: string | number, metricIndex: number) => {
    setSelectedMetricsMap((prev) => {
      const gridMetrics = prev[gridId] || [];
      if (gridMetrics.length <= 1) return prev;
      const updatedMetrics = gridMetrics.filter((_, index) => index !== metricIndex);

      return {
        ...prev,
        [gridId]: updatedMetrics,
      };
    });
  };

  const metricsItems = [
    { label: t('metrics_menu.edit'), value: ACTIONS.EDIT },
    { label: t('metrics_menu.delete'), value: ACTIONS.DELETE },
    // t("metrics_menu.add_edit_metrics"),
  ];

  return (
    <div className={styles.goals_details_container}>
      <div className={styles.goals_details_card}>
        {goalActions.slice(0, 9).map((action: ActionDataResp, index: number) => {
          const shouldShowTopBorder = action.assignToType === 2;

          return (
            <div
              key={action?.aboGoalActionId}
              className={`${styles.action_card_wrapper} ${
                shouldShowTopBorder ? styles.topBorder : ''
              }`}
            >
              {/* Left Side */}
              <div className={styles.left_wrapper}>
                <div className={styles.action_summary}>
                  <span className={styles.action_title}>{action?.title}</span>

                  <div
                    ref={(el) => (statusDropdownRef.current[action?.aboGoalActionId] = el)}
                    className={`status_date_wrapper ${styles.status_date_wrapper}`}
                  >
                    {
                      <StatusDropdown
                        options={GOAL_ACTION_STATUS}
                        selectedItem={
                          selectedActionStatusItem[action?.aboGoalActionId] ||
                          getGoalActionStatusLabel(action?.status) ||
                          'On Track'
                        }
                        setSelectedItem={(item, itemCode) => {
                          updateGoalActionStatus({
                            affiliateCode,
                            aboNumber,
                            goalActionId: action?.aboGoalActionId,
                            statusId: itemCode,
                          }).then((resp: any) => {
                            const updateActionStatusData = resp?.data?.body;
                            if (!isEmpty(updateActionStatusData)) {
                              setSelectedActionStatusItem((prev) => ({
                                ...prev,
                                [action?.aboGoalActionId]: item,
                              }));
                              setShowStatusDropdown((prev) => ({
                                ...prev,
                                [action?.aboGoalActionId]: false,
                              }));
                            }
                          });
                        }}
                      />
                    }
                    {action?.targetDate && (
                      <div className={`target_date_wrapper ${styles.target_date_wrapper}`}>
                        <Icon
                          name="calendar_down"
                          size={14}
                          className={styles.calendar_icon}
                          color={currentTheme === 'light' ? '#707070' : '#AEAEB2'}
                        />
                        <span className={styles.date_value}>
                          {formatDateShort(action?.targetDate || '')}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className={`${styles.action_desctiption}`}>
                    {
                      <span className={styles.input_note}>
                        <HTMLtoText
                          htmlContent={convertUrlsToLinks(action?.note, currentTheme)}
                          alternateMessage=""
                          maxTextLength={100}
                        />
                      </span>
                    }
                  </div>
                </div>

                {/* Right Side */}
                {!isHistoryGoalData && (
                  <div className={styles.right_wrapper}>
                    <div
                      ref={(el) => (metricsDropdownRef.current[action?.aboGoalActionId] = el)}
                      className={`${styles.inner_wrapper} ${
                        showMetricsDropdown[action?.aboGoalActionId] ? styles.active : ''
                      }`}
                    >
                      <Icon
                        name="three_dot_icon"
                        size={12}
                        className={styles.three_dot_icon}
                        onClick={() =>
                          setShowMetricsDropdown((prev) => ({
                            ...prev,
                            [action?.aboGoalActionId]: !prev[action?.aboGoalActionId],
                          }))
                        }
                        color={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
                      />
                      {showMetricsDropdown[action?.aboGoalActionId] && (
                        <DropdownMenu
                          items={metricsItems}
                          selectedItem={selectedMetricsItem[action?.aboGoalActionId]}
                          onSelect={(item: any) => {
                            setSelectedMetricsItem((prev) => ({
                              ...prev,
                              [action?.aboGoalActionId]: item,
                            }));
                            setShowMetricsDropdown((prev) => ({
                              ...prev,
                              [action?.aboGoalActionId]: false,
                            }));
                            handleOperation(item, action?.aboGoalActionId);
                          }}
                          isVisible={showMetricsDropdown[action?.aboGoalActionId]}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom Section - Metrics */}
              <div className={styles.bottom_wrapper}>
                <div className={styles.add_matrics_wrapper}>
                  {
                    (selectedMetricsMap[action?.aboGoalActionId] || action?.goalMetrics)?.length >
                      0 &&
                      (selectedMetricsMap[action?.aboGoalActionId] || action?.goalMetrics).map(
                        (metric) => (
                          <div
                            key={metric.aboGoalActionMetricId}
                            className={`progress_bar_wrapper ${styles.progress_bar_wrapper}`}
                            onMouseEnter={() => {
                              if (!isHistoryGoalData) {
                                setIsHover((prev) => ({
                                  ...prev,
                                  [action?.aboGoalActionId]: {
                                    ...prev[action?.aboGoalActionId],
                                    [metric.aboGoalActionMetricId || '']: true,
                                  },
                                }));
                              }
                            }}
                            onMouseLeave={() =>
                              setIsHover((prev) => ({
                                ...prev,
                                [action?.aboGoalActionId]: {
                                  ...prev[action?.aboGoalActionId],
                                  [metric.aboGoalActionMetricId || '']: false,
                                },
                              }))
                            }
                          >
                            <div className={styles.progress_status}>
                              <div
                                className={styles.progress_bar}
                                onClick={() =>
                                  toggleIsMet(
                                    metric.aboGoalActionId,
                                    metric.aboGoalActionMetricId,
                                    metric.status
                                  )
                                }
                              >
                                <CircularProgressbarWithChildren
                                  value={metric.status === 1 ? 0 : 100}
                                  strokeWidth={10}
                                  className={styles['circular_progress_gradient']}
                                  styles={buildStyles({
                                    trailColor: currentTheme === 'light' ? '#D9D9D9' : '#282828',
                                  })}
                                >
                                  {metric?.status === 2 && (
                                    <Icon
                                      name="check_icon"
                                      size={24}
                                      className={styles.check_icon}
                                      color={currentTheme === 'light' ? '#107F47' : '#B3D67B'}
                                    />
                                  )}
                                  {isHover[action?.aboGoalActionId]?.[
                                    metric.aboGoalActionMetricId || 0
                                  ] &&
                                    metric?.status !== 2 && (
                                      <Icon
                                        name="check_icon"
                                        size={24}
                                        className={`${styles.check_icon} ${styles.dull}`}
                                        color={currentTheme === 'light' ? '#107F47' : '#B3D67B'}
                                      />
                                    )}
                                </CircularProgressbarWithChildren>
                              </div>
                              <div className={styles.matrics_status_wrapper}>
                                <span className={styles.matrics_name}>
                                  {getMetricLabel(metric.kpiCode)}
                                </span>
                                <div className={styles.matrics_status}>
                                  <span className={styles.target_text}>{t('target')}</span>
                                  <div className={styles.progress_status}>
                                    {metric?.status === 2 ? (
                                      <span className={styles.met_text}>{t('met')}</span>
                                    ) : (
                                      <span className={styles.notmet_text}>{t('not_met')}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            {(selectedMetricsMap[action?.aboGoalActionId]?.length ||
                              action.goalMetrics.length) > 1 &&
                              !isHistoryGoalData && (
                                <div className={styles.delete_icon}>
                                  <Icon
                                    name="delete_icon"
                                    size={16}
                                    className={styles.circle_icon}
                                    color={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
                                    onClick={() =>
                                      handleOperation(
                                        ACTIONS.DELETE_METRICS,
                                        metric?.aboGoalActionMetricId || 0
                                      )
                                    }
                                  />
                                </div>
                              )}
                          </div>
                        )
                      )
                    // AMW- 3385- We need to enable these once feature will back in scope
                    //  : !isHistoryGoalData && (
                    //     <div className={styles.matrics_outer_wrapper}>
                    //       <div
                    //       className={`matrics_inner_wrapper ${styles.matrics_inner_wrapper}`}
                    //       onClick={(e) => {
                    //         e.stopPropagation();
                    //         handleOperation(
                    //           ACTIONS.ADD_EDIT_METRICS,
                    //           action?.aboGoalActionId
                    //         );
                    //       }}
                    //     >
                    //       <Icon
                    //         name="plus_icon"
                    //         size={14}
                    //         className={styles.plus_icon}
                    //         color={
                    //           currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"
                    //         }
                    //       />
                    //       <span
                    //         className={`add_metrics_text ${styles.add_metrics_text}`}
                    //       >
                    //         {t("add_metrics")}
                    //       </span>
                    //     </div>
                    //     </div>
                    //   )
                  }
                </div>
              </div>
            </div>
          );
        })}

        {/* Add Action Button */}
        {goalActions.length < 9 && !isHistoryGoalData && (
          <div className={`${styles.add_wrapper}`}>
            <div
              className={styles.add_inner}
              onClick={() => handleOperation(ACTIONS.ADD_ACTIONS, `GRID_${goalActions.length + 1}`)}
            >
              <Icon
                name="plus_icon"
                size={14}
                className={styles.plus_icon}
                color={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
              />
              <span className={`add_action_text ${styles.add_action_text}`}>{t('add_action')}</span>
            </div>
          </div>
        )}
      </div>
      <div className={styles.section_wrapper}>
        <Engagements goalID={goalID} isHistoryGoalData={isHistoryGoalData} />
      </div>
      <div className={styles.section_wrapper}>
        <GoalsNotesAndDocuments goalID={goalID} isHistoryGoalData={isHistoryGoalData} />
      </div>
    </div>
  );
};

export default GoalsDetailsPage;
